import { Candidate, MultipleChoiceQuestionResponse } from '../../../../../firebase/firestore/documents/candidate';
import { Position, PositionMCQ } from '../../../../../firebase/firestore/documents/position';

export function useMCQQuestions({
  candidate,
  position,
}: {
  candidate?: Candidate;
  position: Position;
}): MultipleChoiceQuestionResponse[] {
  // If no position is provided, or candidate has no responses and either monetized or position has no MCQs, return empty array
  if (
    !position ||
    (candidate &&
      !candidate.multipleChoiceQuestionResponses?.length &&
      (candidate.monetized || !position.multipleChoiceQuestions?.length))
  ) {
    return [];
  }

  // Helper function to map PositionMCQ to MultipleChoiceQuestionResponse
  const mapMCQ = (questions: PositionMCQ[]): MultipleChoiceQuestionResponse[] =>
    questions.map((q) => ({
      id: q.id,
      answers: [],
      images: q.images ?? /* istanbul ignore next */ [],
      isAnsweredCorrectly: false,
      knockout: q.knockout,
      question: q.question,
      preferredAnswers: q.preferredAnswers,
      isPausable: q.isPausable,
      options:
        q.options?.map((opt) => ({
          id: opt.id,
          text: opt.text,
          wasAnswered: false,
        })) ?? /* istanbul ignore next */ [],
      totalAnswer: 0,
    }));

  // Case 1: No candidate provided — return all position MCQs mapped
  if (!candidate) {
    return mapMCQ(position.multipleChoiceQuestions ?? []);
  }
  const candidateMCQsQuestions =
    candidate?.multipleChoiceQuestionResponses?.map((el) => {
      const posQuestion = position?.multipleChoiceQuestions?.find((q) => q.id === el.id);
      if (posQuestion) {
        return { ...el, knockout: posQuestion.knockout };
      }
      return { ...el };
    }) ?? /* istanbul ignore next */ [];
  // Case 2: Candidate is monetized — return only existing responses
  if (candidate.monetized) {
    return candidateMCQsQuestions ?? /* istanbul ignore next */ [];
  }

  // Case 3: Candidate with some responses — find unanswered questions
  const unansweredMCQs = (position.multipleChoiceQuestions ?? /* istanbul ignore next */ []).filter(
    (q) => !candidateMCQsQuestions?.some((r) => r.id === q.id),
  );

  // If all questions are already answered, return existing responses
  if (!unansweredMCQs.length) {
    return candidateMCQsQuestions ?? /* istanbul ignore next */ [];
  }

  // Otherwise, combine existing responses with mapped unanswered questions
  return [...(candidateMCQsQuestions ?? /* istanbul ignore next */ []), ...mapMCQ(unansweredMCQs)];
}
